import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { toast } from 'sonner';
import ImageUploader from '../../components/ImageUploader';
import { 
  Save, 
  Home, 
  User, 
  Phone, 
  FileText,
  Settings,
  Globe,
  Image,
  Type,
  RefreshCw,
  Plus,
  Trash2,
  GripVertical
} from 'lucide-react';

const AdminSiteContent = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [content, setContent] = useState({
    // Site-wide settings
    site: {
      name: 'Killarney Connect',
      tagline: 'Progress in Action',
      description: 'Building a stronger community together in Killarney Constituency',
      logo_url: '',
      favicon_url: ''
    },
    // Emergency contacts
    emergency: {
      show_banner: true,
      contacts: [
        { label: 'Emergency', number: '919' },
        { label: 'Airport Police', number: '242-377-8391' },
        { label: 'Cable Beach Police', number: '242-327-8800' }
      ]
    },
    // Homepage content
    homepage: {
      hero_title: 'Together, We Build a Stronger Killarney',
      hero_subtitle: 'Join Robyn Lyons and the PLP in creating positive change for our community',
      hero_button_text: 'Get Involved',
      hero_button_link: '/get-involved',
      hero_secondary_button_text: 'Learn More',
      hero_secondary_button_link: '/about',
      stats: [
        { label: 'Volunteers', key: 'volunteers' },
        { label: 'Issues Fixed', key: 'issues_fixed' },
        { label: 'Safety Reports', key: 'safety_reports' },
        { label: 'Active Issues', key: 'total_issues' }
      ],
      features_title: 'How We Serve Our Community',
      features_subtitle: 'Explore the ways you can get involved and make a difference'
    },
    // About page content
    about: {
      candidate_name: 'Robyn Lyons',
      candidate_title: 'PLP Candidate for Killarney',
      candidate_image: '',
      bio: 'Robyn Lyons is a dedicated community leader committed to serving the people of Killarney constituency. With years of experience in community development and public service, Robyn understands the needs and aspirations of our residents.',
      vision_title: 'Our Vision',
      vision_text: 'A Killarney where every resident has access to quality services, safe neighborhoods, and opportunities for growth.',
      timeline: [
        { year: '2020', event: 'Began community outreach programs' },
        { year: '2021', event: 'Launched youth empowerment initiatives' },
        { year: '2022', event: 'Expanded senior care services' },
        { year: '2023', event: 'Started infrastructure improvement campaigns' }
      ],
      priorities: [
        { title: 'Youth Empowerment', description: 'Creating opportunities for young people through education and job training' },
        { title: 'Community Safety', description: 'Working with police and residents to keep our neighborhoods safe' },
        { title: 'Infrastructure', description: 'Improving roads, drainage, and public facilities' },
        { title: 'Senior Care', description: 'Ensuring our elders receive the support and respect they deserve' }
      ]
    },
    // Footer content
    footer: {
      contact_email: 'info@killarneyconnect.com',
      contact_phone: '242-555-0100',
      contact_address: 'Killarney Constituency Office, Nassau, Bahamas',
      facebook_url: '',
      instagram_url: '',
      twitter_url: '',
      youtube_url: '',
      copyright_text: '© 2024 Killarney Connect. All rights reserved.'
    }
  });

  const fetchContent = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/admin/site-content`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data && Object.keys(response.data).length > 0) {
        setContent(prev => ({ ...prev, ...response.data }));
      }
    } catch (error) {
      console.error('Failed to fetch site content:', error);
      // Use defaults if no content exists yet
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchContent();
  }, [fetchContent]);

  const handleSave = async (section) => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content/${section}`, content[section], {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`${section.charAt(0).toUpperCase() + section.slice(1)} settings saved!`);
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveAll = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-content`, content, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('All site content saved!');
    } catch (error) {
      console.error('Failed to save:', error);
      toast.error('Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (section, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));
  };

  const updateArrayItem = (section, arrayField, index, field, value) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].map((item, i) => 
          i === index ? { ...item, [field]: value } : item
        )
      }
    }));
  };

  const addArrayItem = (section, arrayField, template) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: [...prev[section][arrayField], template]
      }
    }));
  };

  const removeArrayItem = (section, arrayField, index) => {
    setContent(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [arrayField]: prev[section][arrayField].filter((_, i) => i !== index)
      }
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-plp-blue" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="admin-site-content">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Site Content Manager</h1>
          <p className="text-slate-600">Edit all text, images, and content across the entire site</p>
        </div>
        <Button 
          onClick={handleSaveAll} 
          disabled={saving}
          className="bg-plp-blue hover:bg-plp-blue-light"
        >
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save All Changes'}
        </Button>
      </div>

      <Tabs defaultValue="site" className="space-y-6">
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="site" className="flex items-center gap-2">
            <Globe className="w-4 h-4" /> Site
          </TabsTrigger>
          <TabsTrigger value="homepage" className="flex items-center gap-2">
            <Home className="w-4 h-4" /> Home
          </TabsTrigger>
          <TabsTrigger value="about" className="flex items-center gap-2">
            <User className="w-4 h-4" /> About
          </TabsTrigger>
          <TabsTrigger value="emergency" className="flex items-center gap-2">
            <Phone className="w-4 h-4" /> Emergency
          </TabsTrigger>
          <TabsTrigger value="footer" className="flex items-center gap-2">
            <FileText className="w-4 h-4" /> Footer
          </TabsTrigger>
        </TabsList>

        {/* Site Settings */}
        <TabsContent value="site">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" /> Site-Wide Settings
              </CardTitle>
              <CardDescription>Basic settings that appear across the entire site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <Label>Site Name</Label>
                  <Input
                    value={content.site.name}
                    onChange={(e) => updateContent('site', 'name', e.target.value)}
                    placeholder="Killarney Connect"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Tagline</Label>
                  <Input
                    value={content.site.tagline}
                    onChange={(e) => updateContent('site', 'tagline', e.target.value)}
                    placeholder="Progress in Action"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Site Description</Label>
                <Textarea
                  value={content.site.description}
                  onChange={(e) => updateContent('site', 'description', e.target.value)}
                  placeholder="Brief description of the site"
                  rows={3}
                />
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <ImageUploader
                  value={content.site.logo_url}
                  onChange={(url) => updateContent('site', 'logo_url', url)}
                  label="Site Logo"
                />
                <ImageUploader
                  value={content.site.favicon_url}
                  onChange={(url) => updateContent('site', 'favicon_url', url)}
                  label="Favicon"
                />
              </div>
              <Button onClick={() => handleSave('site')} disabled={saving}>
                <Save className="w-4 h-4 mr-2" /> Save Site Settings
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Homepage Content */}
        <TabsContent value="homepage">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Type className="w-5 h-5" /> Hero Section
                </CardTitle>
                <CardDescription>The main banner area at the top of the homepage</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Hero Title</Label>
                  <Input
                    value={content.homepage.hero_title}
                    onChange={(e) => updateContent('homepage', 'hero_title', e.target.value)}
                    placeholder="Main headline"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Hero Subtitle</Label>
                  <Textarea
                    value={content.homepage.hero_subtitle}
                    onChange={(e) => updateContent('homepage', 'hero_subtitle', e.target.value)}
                    placeholder="Supporting text"
                    rows={2}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Primary Button Text</Label>
                    <Input
                      value={content.homepage.hero_button_text}
                      onChange={(e) => updateContent('homepage', 'hero_button_text', e.target.value)}
                      placeholder="Get Involved"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Primary Button Link</Label>
                    <Input
                      value={content.homepage.hero_button_link}
                      onChange={(e) => updateContent('homepage', 'hero_button_link', e.target.value)}
                      placeholder="/get-involved"
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Secondary Button Text</Label>
                    <Input
                      value={content.homepage.hero_secondary_button_text}
                      onChange={(e) => updateContent('homepage', 'hero_secondary_button_text', e.target.value)}
                      placeholder="Learn More"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Secondary Button Link</Label>
                    <Input
                      value={content.homepage.hero_secondary_button_link}
                      onChange={(e) => updateContent('homepage', 'hero_secondary_button_link', e.target.value)}
                      placeholder="/about"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Features Section</CardTitle>
                <CardDescription>The section titles for the features area</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label>Features Title</Label>
                  <Input
                    value={content.homepage.features_title}
                    onChange={(e) => updateContent('homepage', 'features_title', e.target.value)}
                    placeholder="How We Serve Our Community"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Features Subtitle</Label>
                  <Input
                    value={content.homepage.features_subtitle}
                    onChange={(e) => updateContent('homepage', 'features_subtitle', e.target.value)}
                    placeholder="Explore the ways you can get involved"
                  />
                </div>
              </CardContent>
            </Card>

            <Button onClick={() => handleSave('homepage')} disabled={saving} className="bg-plp-blue hover:bg-plp-blue-light">
              <Save className="w-4 h-4 mr-2" /> Save Homepage Content
            </Button>
          </div>
        </TabsContent>

        {/* About Page Content */}
        <TabsContent value="about">
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User className="w-5 h-5" /> Candidate Information
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Candidate Name</Label>
                    <Input
                      value={content.about.candidate_name}
                      onChange={(e) => updateContent('about', 'candidate_name', e.target.value)}
                      placeholder="Robyn Lyons"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Title</Label>
                    <Input
                      value={content.about.candidate_title}
                      onChange={(e) => updateContent('about', 'candidate_title', e.target.value)}
                      placeholder="PLP Candidate for Killarney"
                    />
                  </div>
                </div>
                <ImageUploader
                  value={content.about.candidate_image}
                  onChange={(url) => updateContent('about', 'candidate_image', url)}
                  label="Candidate Photo"
                />
                <div className="space-y-2">
                  <Label>Biography</Label>
                  <Textarea
                    value={content.about.bio}
                    onChange={(e) => updateContent('about', 'bio', e.target.value)}
                    placeholder="Candidate biography..."
                    rows={5}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Vision Title</Label>
                    <Input
                      value={content.about.vision_title}
                      onChange={(e) => updateContent('about', 'vision_title', e.target.value)}
                      placeholder="Our Vision"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Vision Text</Label>
                  <Textarea
                    value={content.about.vision_text}
                    onChange={(e) => updateContent('about', 'vision_text', e.target.value)}
                    placeholder="Vision statement..."
                    rows={3}
                  />
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Timeline</CardTitle>
                <CardDescription>Key milestones and achievements</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.about.timeline.map((item, idx) => (
                  <div key={`timeline-${idx}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <GripVertical className="w-5 h-5 text-slate-400 mt-2" />
                    <div className="flex-1 grid md:grid-cols-4 gap-3">
                      <Input
                        value={item.year}
                        onChange={(e) => updateArrayItem('about', 'timeline', idx, 'year', e.target.value)}
                        placeholder="Year"
                      />
                      <Input
                        value={item.event}
                        onChange={(e) => updateArrayItem('about', 'timeline', idx, 'event', e.target.value)}
                        placeholder="Event description"
                        className="md:col-span-3"
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('about', 'timeline', idx)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addArrayItem('about', 'timeline', { year: '', event: '' })}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Timeline Item
                </Button>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle>Priorities</CardTitle>
                <CardDescription>Key focus areas and initiatives</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {content.about.priorities.map((item, idx) => (
                  <div key={`priority-${idx}`} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                    <GripVertical className="w-5 h-5 text-slate-400 mt-2" />
                    <div className="flex-1 space-y-2">
                      <Input
                        value={item.title}
                        onChange={(e) => updateArrayItem('about', 'priorities', idx, 'title', e.target.value)}
                        placeholder="Priority title"
                      />
                      <Textarea
                        value={item.description}
                        onChange={(e) => updateArrayItem('about', 'priorities', idx, 'description', e.target.value)}
                        placeholder="Description"
                        rows={2}
                      />
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removeArrayItem('about', 'priorities', idx)}
                      className="text-red-500"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                ))}
                <Button
                  variant="outline"
                  onClick={() => addArrayItem('about', 'priorities', { title: '', description: '' })}
                >
                  <Plus className="w-4 h-4 mr-2" /> Add Priority
                </Button>
              </CardContent>
            </Card>

            <Button onClick={() => handleSave('about')} disabled={saving} className="bg-plp-blue hover:bg-plp-blue-light">
              <Save className="w-4 h-4 mr-2" /> Save About Content
            </Button>
          </div>
        </TabsContent>

        {/* Emergency Contacts */}
        <TabsContent value="emergency">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Phone className="w-5 h-5" /> Emergency Contacts
              </CardTitle>
              <CardDescription>Emergency numbers displayed at the top of the site</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                <input
                  type="checkbox"
                  checked={content.emergency.show_banner}
                  onChange={(e) => updateContent('emergency', 'show_banner', e.target.checked)}
                  className="w-4 h-4"
                />
                <Label>Show emergency contacts banner at top of site</Label>
              </div>
              
              {content.emergency.contacts.map((contact, idx) => (
                <div key={`contact-${idx}`} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                  <GripVertical className="w-5 h-5 text-slate-400" />
                  <div className="flex-1 grid md:grid-cols-2 gap-3">
                    <Input
                      value={contact.label}
                      onChange={(e) => updateArrayItem('emergency', 'contacts', idx, 'label', e.target.value)}
                      placeholder="Label (e.g., Emergency)"
                    />
                    <Input
                      value={contact.number}
                      onChange={(e) => updateArrayItem('emergency', 'contacts', idx, 'number', e.target.value)}
                      placeholder="Phone number"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={() => removeArrayItem('emergency', 'contacts', idx)}
                    className="text-red-500"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              ))}
              <Button
                variant="outline"
                onClick={() => addArrayItem('emergency', 'contacts', { label: '', number: '' })}
              >
                <Plus className="w-4 h-4 mr-2" /> Add Contact
              </Button>
              
              <Button onClick={() => handleSave('emergency')} disabled={saving} className="bg-plp-blue hover:bg-plp-blue-light">
                <Save className="w-4 h-4 mr-2" /> Save Emergency Contacts
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Content */}
        <TabsContent value="footer">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5" /> Footer Content
              </CardTitle>
              <CardDescription>Contact information and social links in the footer</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contact Email</Label>
                  <Input
                    value={content.footer.contact_email}
                    onChange={(e) => updateContent('footer', 'contact_email', e.target.value)}
                    placeholder="info@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Contact Phone</Label>
                  <Input
                    value={content.footer.contact_phone}
                    onChange={(e) => updateContent('footer', 'contact_phone', e.target.value)}
                    placeholder="242-555-0100"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Address</Label>
                <Textarea
                  value={content.footer.contact_address}
                  onChange={(e) => updateContent('footer', 'contact_address', e.target.value)}
                  placeholder="Office address"
                  rows={2}
                />
              </div>
              
              <div className="border-t pt-4">
                <Label className="text-base font-semibold mb-3 block">Social Media Links</Label>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Facebook URL</Label>
                    <Input
                      value={content.footer.facebook_url}
                      onChange={(e) => updateContent('footer', 'facebook_url', e.target.value)}
                      placeholder="https://facebook.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Instagram URL</Label>
                    <Input
                      value={content.footer.instagram_url}
                      onChange={(e) => updateContent('footer', 'instagram_url', e.target.value)}
                      placeholder="https://instagram.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Twitter/X URL</Label>
                    <Input
                      value={content.footer.twitter_url}
                      onChange={(e) => updateContent('footer', 'twitter_url', e.target.value)}
                      placeholder="https://twitter.com/..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>YouTube URL</Label>
                    <Input
                      value={content.footer.youtube_url}
                      onChange={(e) => updateContent('footer', 'youtube_url', e.target.value)}
                      placeholder="https://youtube.com/..."
                    />
                  </div>
                </div>
              </div>
              
              <div className="space-y-2">
                <Label>Copyright Text</Label>
                <Input
                  value={content.footer.copyright_text}
                  onChange={(e) => updateContent('footer', 'copyright_text', e.target.value)}
                  placeholder="© 2024 Killarney Connect. All rights reserved."
                />
              </div>
              
              <Button onClick={() => handleSave('footer')} disabled={saving} className="bg-plp-blue hover:bg-plp-blue-light">
                <Save className="w-4 h-4 mr-2" /> Save Footer Content
              </Button>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AdminSiteContent;
